import moment from 'moment'
import api from '../api'
import { fetchRecentActivities, updateRecentActivities } from './recentActivities'
import { fetchQuestionPolls } from './polls'
import { fetchLibraryPair } from './libraryHelpers'
import store from '../store'

const MINIMUM_TIME_BETWEEN_REFRESHES = 1000 * 30 // 30seconds

// state.questions holds the union of these two requests. Both derive their expected
// count from the same URL they load from, so the count matches the filters applied.
const QUESTIONS_URLS = {
  own: '/questions?archived=false&parentArchived=false&sort=modified',
  allRepos: '/questions?allRepos=true&archived=false&parentArchived=false&sort=modified',
}

function loadQuestions(dispatch, modifiedAfter) {
  fetchLibraryPair(dispatch, {
    collection: 'questions',
    urls: QUESTIONS_URLS,
    successType: modifiedAfter ? 'REFRESH_QUESTIONS_SUCCESS' : 'FETCH_QUESTIONS_SUCCESS',
    modifiedAfter,
  })
}

export function fetchAQuestion(questionId) {
  const state = store.getState()
  if (state.requestedQuestions[questionId]) {
    return ({ type: 'WAIT_TO_REFRESH' })
  }
  return (dispatch) => {
    dispatch({ type: 'FETCH_QUESTION_REQUEST', questionId })
    api.fetch(`/questions/${questionId}`)
      .then((response) => {
        dispatch({ type: 'FETCH_A_QUESTION_SUCCESS', response })
        return response
      })
      .catch(() => {})
  }
}

export function fetchQuestions() {
  const state = store.getState()
  if (state.questionsRefreshedTimestamp) {
    const nowDate = moment()
    const timeSinceLastRefresh = nowDate - moment(state.questionsRefreshedTimestamp)
    if (timeSinceLastRefresh > MINIMUM_TIME_BETWEEN_REFRESHES) {
      return (dispatch) => {
        loadQuestions(dispatch, state.questionsRefreshedTimestamp)
      }
    }
    return ({ type: 'WAIT_TO_REFRESH' })
  }
  // no questionsRefreshedTimestamp (first request)
  return (dispatch) => {
    loadQuestions(dispatch)
  }
}

// For displaying pack preview to user who has invitation pending or disabled access
export function fetchQuestionsPreview() {
  return (dispatch) => api.fetch('/questions/preview')
    .then((response) => {
      dispatch({ type: 'FETCH_QUESTIONS_PREVIEW_SUCCESS', response })
    })
    .catch(() => {})
}

export function fetchQuestionsNoMinTime() { // force fetch regardless of questionsRefreshedTimestamp
  // TODO clean this up
  const state = store.getState()
  if (state.questionsRefreshedTimestamp) {
    return (dispatch) => {
      loadQuestions(dispatch, state.questionsRefreshedTimestamp)
    }
  }
  return (dispatch) => {
    loadQuestions(dispatch)
  }
}

export function fetchQuestionsForRepo(repoId) {
  return (dispatch) => api.batchedFetch(`/questions?archived=false&sort=modified&parentArchived=false&repo=${repoId}`)
    .then((response) => {
      dispatch({ type: 'FETCH_REPO_QUESTIONS_SUCCESS', response })
    })
    .catch(() => {})
}

export function refetchQuestions() { // TODO combine with fetchQuestionsNoMinTime
  return (dispatch) => {
    api.batchedFetch('/questions?archived=false&parentArchived=false&sort=modified')
      .then((response) => {
        dispatch({ type: 'FETCH_QUESTIONS_SUCCESS', response })
      })
      .catch(() => {})

    api.batchedFetch('/questions?allRepos=true&archived=false&parentArchived=false&sort=modified')
      .then((response) => {
        dispatch({ type: 'FETCH_QUESTIONS_SUCCESS', response })
      })
      .catch(() => {})
  }
}

export function fetchArchivedQuestions() {
  return (dispatch) => {
    api.batchedFetch('/questions?archived=true&parentArchived=false&sort=modified')
      .then((response) => {
        dispatch({ type: 'FETCH_ARCHIVED_QUESTIONS_SUCCESS', response })
      })
      .catch(() => {})

    api.batchedFetch('/questions?allRepos=true&archived=true&parentArchived=false&sort=modified')
      .then((response) => {
        dispatch({ type: 'FETCH_ARCHIVED_QUESTIONS_SUCCESS', response })
      })
      .catch(() => {})
  }
}

export function fetchArchivedQuestionsForRepo(repoId) {
  return (dispatch) => api.batchedFetch(`/questions?archived=true&parentArchived=false&sort=modified&repo=${repoId}`)
    .then((response) => {
      dispatch({ type: 'FETCH_ARCHIVED_QUESTIONS_SUCCESS', response })
    })
    .catch(() => {})
}

export function createNewQuestion(data) {
  if (typeof data.folder !== 'string') { // Prevent invalid folder value
    data.folder = null
  }
  data.lastEditedAt = data.clientModified
  return (dispatch) => api.post('/questions/', data)
    .then((question) => {
      dispatch({ type: 'CREATE_QUESTION_SUCCESS', question, syncAction: true })
      dispatch(updateRecentActivities(question.id, 'question', 'create'))
      return question
    })
}

export function deleteQuestion(questionId) {
  return (dispatch) => api.delete(`/questions/${questionId}`)
    .then((response) => {
      dispatch({ type: 'DELETE_QUESTION_SUCCESS', questionId, syncAction: true })
      dispatch(fetchQuestionPolls())
      dispatch(fetchRecentActivities())
      return response
    })
    .catch(() => 'failed')
}

export function updateQuestionContent(data, questionId) {
  data.lastEditedAt = data.clientModified
  return (dispatch) => api.put(`/questions/${questionId}/content`, data)
    .then((response) => {
      dispatch({ type: 'UPDATE_QUESTION_SUCCESS', response, syncAction: true })
      if (!data.archived) {
        dispatch(updateRecentActivities(response.id, 'question', 'edit'))
      }
      return response
    })
    .catch(() => 'failed')
}

export function updateQuestionMeta(data, questionId) {
  if (typeof data.folder !== 'string') { // Prevent invalid folder value
    data.folder = null
  }
  return (dispatch) => api.put(`/questions/${questionId}/meta`, data)
    .then((response) => {
      dispatch({ type: 'UPDATE_QUESTION_SUCCESS', response, syncAction: true })
      if (!data.archived) {
        dispatch(updateRecentActivities(response.id, 'question', 'edit'))
      }
      return response
    })
    .catch(() => 'failed')
}
